// Adapter backend catalog -> data structures of the reference tool (SCREWS_DB records with the exact TC_SCREWS_DB
// shape, the buildTimberDb() tree, WASHER_DB, KMOD, GAMMA_M_*), so the ported logic can stay 1:1 with the HTML.

import type { ScrewFamilyForUser } from "../types/screwFamilies";
import type { ScrewDiameter } from "../types/screwDiameters";
import type { Screw } from "../types/screws";
import type { Washer } from "../types/washers";
import type { TimberMaterialForUser } from "../types/timberMaterials";
import type { GammaMValue, KmodValue } from "../types/normativeTables";
import {
  GAMMA_M_EC5_DEFAULT,
  GAMMA_M_NTC2018_DEFAULT,
  type GammaMEc5Table,
  type GammaMNtcTable,
  KMOD_DEFAULT,
  type KmodTable,
  LOAD_DURATION_TO_HTML,
  MODULE_NAME,
  SCREW_FAMILY_ORDER,
  SUBSTRATE_NAME,
  TIMBER_CATEGORY_ORDER,
  TIMBER_CLASS_ORDER,
  TIMBER_SUBCAT_FALLBACK,
  TIMBER_TIPO_MAP,
} from "./data";

export type RawCatalog = {
  families: ScrewFamilyForUser[];
  diameters: ScrewDiameter[];
  screws: Screw[];
  washers: Washer[];
  timberMaterials: TimberMaterialForUser[];
  kmodValues: KmodValue[];
  gammaMValues: GammaMValue[];
};

export type ScrewResistanceRow = {
  substrate: string; // e.g. "Solidwood Softwood", "Solidwood Hardwood - OAK, BEECH"
  preforo: boolean;
  param: "fax,k" | "fhead,k";
  value_Nmm2: number | null;
  rhoA_kgm3: number | null;
  rhoCalc_kgm3: string | null;
};

// One record of the source TC_SCREWS_DB (+ backend ids).
export type ScrewRecord = {
  id: string; // screw item id (CalculationInput.screwId)
  diameterId: string;
  familyId: string;
  sku: string;
  family: string;
  descrizioneCatalogo: string;
  tipoVite: string;
  threadType: "partial" | "full";
  headType: string;
  tipType: string | null;
  geometria_mm: {
    d1: number;
    d2: number;
    ds: number | null;
    dk: number;
    L: number;
    LG1: number;
    LG2: number;
    Ltip: number;
  };
  bit: string;
  immagine: string | null;
  rondelleCompatibili: string; // comma-separated washer codes, "none" when none
  materiale: {
    material: string;
    coating: string | null;
    CC: string | null;
    TC: string | null;
    SC_EU: string | null;
    EC_US: string | null;
    WD_US: string | null;
  };
  normativa: string | null;
  supportiCompatibiliEU: Record<string, number>;
  moduliCompatibili: Record<string, number>;
  resistenza: { MyK_Nmm: number; ftensK_kN: number };
  estrazioneAssiale: ScrewResistanceRow[];
  estrazioneTesta: ScrewResistanceRow[];
};

export type TimberClassProps = {
  rhok: number | null;
  rhomean: number | null;
  fc0k: number | null;
  fc90k: number | null;
  frk: number | null;
  fvk: number | null;
  rif: string | null;
  tipo: string;
  materialId: string;
};
// TIMBER_DB[tipo][specie][classe]
export type TimberDb = Record<string, Record<string, Record<string, TimberClassProps>>>;
export type MaterialPath = { tipo: string; specie: string; classe: string };
export type WasherInfo = { d1: number; d2: number; h: number };

export type EngineCatalog = {
  SCREWS_DB: ScrewRecord[];
  TIMBER_DB: TimberDb;
  WASHER_DB: Record<string, WasherInfo>;
  KMOD: KmodTable;
  GAMMA_M_EC5: GammaMEc5Table;
  GAMMA_M_NTC2018: GammaMNtcTable;
  // lookups (the HTML screwIndexByKey is built lazily; here once per catalog)
  screwIndexByKey: Map<string, ScrewRecord>; // family|d1|L
  screwById: Map<string, ScrewRecord>;
  materialById: Map<string, MaterialPath>;
};

const orderIndex = (list: string[], value: string): number => {
  const i = list.indexOf(value);
  return i < 0 ? list.length : i;
};

function resistanceRows(d: ScrewDiameter, param: "fax_k" | "fhead_k"): ScrewResistanceRow[] {
  return (d.resistanceValues ?? [])
    .filter((r) => r.resistanceParam === param)
    .map((r): ScrewResistanceRow => ({
      substrate: (SUBSTRATE_NAME[r.substrateCategory] ?? r.substrateCategory) + (r.substrateSpecies ? " - " + r.substrateSpecies : ""),
      preforo: r.preDrilled,
      param: param === "fax_k" ? "fax,k" : "fhead,k",
      value_Nmm2: r.valueNMm2,
      rhoA_kgm3: r.referenceDensity,
      rhoCalc_kgm3: r.densityRange,
    }));
}

function buildScrewsDb(raw: RawCatalog): ScrewRecord[] {
  const familyById = new Map(raw.families.map((f) => [f.id, f] as const));
  const diameterById = new Map(raw.diameters.map((d) => [d.id, d] as const));
  const famIdx = new Map(raw.families.map((f, i) => [f.id, i] as const));
  const records: { rec: ScrewRecord; fam: ScrewFamilyForUser }[] = [];
  for (const s of raw.screws) {
    const d = diameterById.get(s.screwDiameterId);
    const f = d ? familyById.get(d.screwFamilyId) : undefined;
    if (!d || !f) continue;
    const supporti: Record<string, number> = {};
    for (const c of f.substrates ?? []) supporti[SUBSTRATE_NAME[c.substrateCategory] ?? c.substrateCategory] = c.compatibilityLevel;
    const moduli: Record<string, number> = {};
    for (const c of f.connectionTypes ?? []) moduli[MODULE_NAME[c.connectionType] ?? c.connectionType] = c.compatibilityLevel;
    const washers = d.washerCodes ?? [];
    records.push({
      fam: f,
      rec: {
        id: s.item.id,
        diameterId: d.id,
        familyId: f.id,
        sku: s.item.code || `${f.name}${d.diameter}${s.length}`.replace(/[\s.]/g, ""),
        family: f.name,
        descrizioneCatalogo: f.description ?? "",
        // TC_SCREWS_DB format ("HBS - Vite a testa svasata Ø3,5 x 40"), not the item's (English) description
        tipoVite: `${f.name} - ${f.description ?? ""} Ø${String(d.diameter).replace(".", ",")} x ${s.length}`,
        threadType: f.threadType,
        headType: d.headType,
        tipType: d.tipType,
        geometria_mm: {
          d1: d.diameter,
          d2: d.coreDiameter,
          ds: d.threadOuterDiameter,
          dk: d.headDiameter,
          L: s.length,
          LG1: s.partialThreadLength ?? NaN,
          LG2: s.fullThreadExtraLength ?? 0,
          Ltip: s.tipLength ?? NaN,
        },
        bit: d.bit,
        immagine: f.image,
        rondelleCompatibili: washers.length ? washers.join(", ") : "none",
        materiale: {
          material: f.material,
          coating: f.coating,
          CC: f.ccCode,
          TC: f.tcCode,
          SC_EU: f.scEuCode,
          EC_US: f.ecUsCode,
          WD_US: f.wdUsCode,
        },
        normativa: f.standardReference,
        supportiCompatibiliEU: supporti,
        moduliCompatibili: moduli,
        resistenza: { MyK_Nmm: d.yieldMoment, ftensK_kN: d.tensileCapacity },
        estrazioneAssiale: resistanceRows(d, "fax_k"),
        estrazioneTesta: resistanceRows(d, "fhead_k"),
      },
    });
  }
  // TC_SCREWS_DB order: family (admin order, then source order, then API order), diameter, length.
  records.sort(
    (a, b) =>
      (a.fam.order ?? 50) - (b.fam.order ?? 50) ||
      orderIndex(SCREW_FAMILY_ORDER, a.fam.name) - orderIndex(SCREW_FAMILY_ORDER, b.fam.name) ||
      (famIdx.get(a.fam.id) ?? 0) - (famIdx.get(b.fam.id) ?? 0) ||
      a.rec.geometria_mm.d1 - b.rec.geometria_mm.d1 ||
      a.rec.geometria_mm.L - b.rec.geometria_mm.L,
  );
  return records.map((r) => r.rec);
}

// buildTimberDb(): Tipo di materiale -> Specie legnosa -> Classe -> properties (EU rows only, rows without class
// skipped). Sorted like TC_TIMBER_DB so Object.keys() order (= menu order, first option = default) matches.
function buildTimberDb(materials: TimberMaterialForUser[]): { db: TimberDb; byId: Map<string, MaterialPath> } {
  const idx = new Map(materials.map((m, i) => [m.id, i] as const));
  const rows = materials
    .filter((m) => !!m.class)
    .slice()
    .sort(
      (a, b) =>
        orderIndex(TIMBER_CATEGORY_ORDER, a.materialCategory) - orderIndex(TIMBER_CATEGORY_ORDER, b.materialCategory) ||
        (a.order ?? 50) - (b.order ?? 50) ||
        orderIndex(TIMBER_CLASS_ORDER, a.class) - orderIndex(TIMBER_CLASS_ORDER, b.class) ||
        (idx.get(a.id) ?? 0) - (idx.get(b.id) ?? 0),
    );
  const db: TimberDb = {};
  const byId = new Map<string, MaterialPath>();
  for (const row of rows) {
    const cat = SUBSTRATE_NAME[row.materialCategory] ?? row.materialCategory;
    const tipo = TIMBER_TIPO_MAP[cat] || cat;
    const specie = row.species || TIMBER_SUBCAT_FALLBACK[tipo] || row.class;
    const tipoNode = (db[tipo] ??= {});
    const specieNode = (tipoNode[specie] ??= {});
    if (specieNode[row.class]) continue;
    specieNode[row.class] = {
      rhok: row.characteristicDensity,
      rhomean: row.meanDensity,
      fc0k: row.compressionStrength0,
      fc90k: row.compressionStrength90,
      frk: row.rollingShearStrength,
      fvk: row.shearStrength,
      rif: row.standardReference,
      tipo: row.materialCategory === "panels" ? "Pannello" : row.materialCategory === "steel" ? "Acciaio" : "Legno",
      materialId: row.id,
    };
    byId.set(row.id, { tipo, specie, classe: row.class });
  }
  return { db, byId };
}

function buildKmod(values: KmodValue[]): KmodTable {
  const out: KmodTable = {};
  for (const sc of [1, 2, 3]) out[sc] = { ...KMOD_DEFAULT[sc] };
  for (const v of values) {
    const key = LOAD_DURATION_TO_HTML[v.loadDurationClassCode] ?? v.loadDurationClassCode;
    const row = (out[v.serviceClassCode] ??= {});
    row[key] = v.kmod;
  }
  return out;
}

export function buildEngineCatalog(raw: RawCatalog): EngineCatalog {
  const SCREWS_DB = buildScrewsDb(raw);
  const { db: TIMBER_DB, byId: materialById } = buildTimberDb(raw.timberMaterials);
  const WASHER_DB: Record<string, WasherInfo> = {};
  for (const w of raw.washers) WASHER_DB[w.code] = { d1: w.innerDiameter, d2: w.outerDiameter, h: w.height };
  const GAMMA_M_EC5 = { ...GAMMA_M_EC5_DEFAULT };
  const GAMMA_M_NTC2018 = { ...GAMMA_M_NTC2018_DEFAULT };
  for (const g of raw.gammaMValues) {
    if (g.standard === "EC5") GAMMA_M_EC5["connessioni"] = { label: "Connessioni", val: g.columnA };
    if (g.standard === "NTC2018") GAMMA_M_NTC2018["unioni"] = { label: "Unioni", A: g.columnA, B: g.columnB };
  }
  const screwIndexByKey = new Map<string, ScrewRecord>();
  for (const s of SCREWS_DB) {
    const key = s.family + "|" + s.geometria_mm.d1 + "|" + s.geometria_mm.L;
    // the HTML index keeps the LAST record of a key (Map.set), currentScrewRecord() the first match
    screwIndexByKey.set(key, s);
  }
  return {
    SCREWS_DB,
    TIMBER_DB,
    WASHER_DB,
    KMOD: buildKmod(raw.kmodValues),
    GAMMA_M_EC5,
    GAMMA_M_NTC2018,
    screwIndexByKey,
    screwById: new Map(SCREWS_DB.map((s) => [s.id, s] as const)),
    materialById,
  };
}

// Backend material id of a Tipo/Specie/Classe selection (null when not in the catalog).
export function materialIdOf(cat: EngineCatalog, tipo: string, specie: string, classe: string): string | null {
  return cat.TIMBER_DB[tipo]?.[specie]?.[classe]?.materialId ?? null;
}
